import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { X, Upload, Play, Square, Trash2, RotateCw } from 'lucide-react';
import apiService from '../services/api';

interface FirmwareChip {
  id: number;
  name: string;
  machineId: number;
  machine?: { number: string } | null;
}

type JobStatus = 'pending' | 'sent' | 'downloading' | 'flashed' | 'done' | 'failed' | 'cancelled';

interface Job {
  id: number;
  chipId: number;
  tenantId: number;
  machineId: number;
  version: string;
  size: number;
  md5: string;
  status: JobStatus;
  message: string | null;
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
}

interface JobsResponse {
  jobs: Job[];
  chipVersion: string | null;
  chipLastOnlineAt: string | null;
}

interface LogLine {
  seq: number;
  at: string;
  line: string;
}

interface ChipFirmwareModalProps {
  chip: FirmwareChip;
  onClose: () => void;
}

const MAX_LOG_LINES = 1000;
const JOBS_POLL_MS = 3000;
const LOGS_POLL_MS = 1000;

const STATUS_BADGE: Record<JobStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  downloading: 'bg-blue-100 text-blue-800',
  flashed: 'bg-indigo-100 text-indigo-800',
  done: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

// Prefer the server's `error` field (e.g. "this .bin was built for another chip").
const serverError = (err: any, fallback: string): string =>
  err?.response?.data?.error || err?.response?.data?.message || fallback;

const formatTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');

const formatLogTime = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
};

const ChipFirmwareModal: React.FC<ChipFirmwareModalProps> = ({ chip, onClose }) => {
  const { t } = useTranslation();

  // ---- Upload ----
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  // ---- Updates (jobs), polled while the modal is open ----
  const jobsQuery = useQuery({
    queryKey: ['chip-firmware-jobs', chip.id],
    queryFn: (): Promise<JobsResponse> => apiService.get(`/firmware/chips/${chip.id}/jobs`),
    refetchInterval: JOBS_POLL_MS,
  });
  const jobs: Job[] = jobsQuery.data?.jobs ?? [];

  // ---- Live logs ----
  const [live, setLive] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [logError, setLogError] = useState('');
  const [logLines, setLogLines] = useState<LogLine[]>([]);
  const [restarting, setRestarting] = useState(false);
  const [restartMsg, setRestartMsg] = useState('');
  const lastSeqRef = useRef(0);
  const liveRef = useRef(false);
  const logBoxRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    liveRef.current = live;
  }, [live]);

  // Poll for new lines while live. setTimeout chaining (not setInterval) so a slow
  // request never overlaps the next one.
  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const res = await apiService.get(`/firmware/chips/${chip.id}/logs?after=${lastSeqRef.current}`);
        if (cancelled) return;
        // Backend restarted (its log seq began again from 0): restart our cursor too
        if (typeof res?.last === 'number' && res.last < lastSeqRef.current) lastSeqRef.current = 0;
        const after = lastSeqRef.current;
        const fresh: LogLine[] = (res?.lines || []).filter((l: LogLine) => l.seq > after);
        if (fresh.length) {
          setLogLines((prev) => {
            const next = prev.concat(fresh);
            return next.length > MAX_LOG_LINES ? next.slice(-MAX_LOG_LINES) : next;
          });
        }
        const maxSeq = fresh.reduce((m, l) => Math.max(m, l.seq), after);
        // Cursor only moves forward, so a line is never appended twice.
        lastSeqRef.current = Math.max(maxSeq, typeof res?.last === 'number' ? res.last : 0);
        setLogError('');
      } catch (err) {
        if (!cancelled) setLogError(t('chipFirmware.logsPollFailed'));
      }
      if (!cancelled) timer = setTimeout(poll, LOGS_POLL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [live, chip.id, t]);

  // Closing (unmounting) the modal while live tells the chip to stop streaming.
  useEffect(() => {
    return () => {
      if (liveRef.current) {
        liveRef.current = false;
        apiService.post(`/firmware/chips/${chip.id}/logs`, { on: false }).catch(() => {});
      }
    };
  }, [chip.id]);

  // Auto-scroll to the newest line unless the user scrolled up to read.
  useLayoutEffect(() => {
    const el = logBoxRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [logLines]);

  const handleLogScroll = () => {
    const el = logBoxRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  const handleToggleLive = async () => {
    const on = !live;
    setToggling(true);
    setLogError('');
    try {
      await apiService.post(`/firmware/chips/${chip.id}/logs`, { on });
      if (on) stickToBottomRef.current = true;
      setLive(on);
    } catch (err) {
      setLogError(serverError(err, t('chipFirmware.logsToggleFailed')));
      // If "stop" failed, stop polling anyway; the chip times out by itself after 30 min.
      if (!on) setLive(false);
    } finally {
      setToggling(false);
    }
  };

  const handleRestart = async () => {
    if (!window.confirm(t('chipFirmware.restartConfirm'))) return;
    setRestarting(true);
    setRestartMsg('');
    try {
      await apiService.post(`/firmware/chips/${chip.id}/restart`, {});
      setRestartMsg(t('chipFirmware.restartSent'));
    } catch (err) {
      setRestartMsg(serverError(err, t('chipFirmware.restartFailed')));
    } finally {
      setRestarting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null);
    setUploadError('');
    setUploadSuccess('');
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.bin')) {
      setUploadError(t('chipFirmware.notBin'));
      return;
    }
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      const formData = new FormData();
      formData.append('firmware', file);
      await apiService.upload(`/firmware/chips/${chip.id}/upload`, formData);
      setUploadSuccess(t('chipFirmware.uploadSuccess'));
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      jobsQuery.refetch();
    } catch (err) {
      setUploadError(serverError(err, t('chipFirmware.uploadFailed')));
    } finally {
      setUploading(false);
    }
  };

  const sectionTitleBase = 'text-sm font-semibold text-gray-900 uppercase tracking-wider';
  const sectionTitle = `${sectionTitleBase} mb-3`;
  const secondaryBtn =
    'inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">
              {t('chipFirmware.title')} — {chip.name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {chip.machine?.number != null && <>{t('chipFirmware.machine', { number: chip.machine.number })} · </>}
              <span className="font-mono">{t('chipFirmware.chipIdC', { id: chip.machineId })}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title={t('chipFirmware.close')}
            aria-label={t('chipFirmware.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* Upload */}
          <section>
            <h3 className={sectionTitle}>{t('chipFirmware.uploadTitle')}</h3>
            {uploadError && (
              <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{uploadError}</div>
            )}
            {uploadSuccess && (
              <div className="mb-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded">{uploadSuccess}</div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".bin"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
              <button
                type="button"
                onClick={handleUpload}
                disabled={!file || uploading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 whitespace-nowrap"
              >
                <Upload className="w-4 h-4" />
                {uploading ? t('chipFirmware.uploading') : t('chipFirmware.upload')}
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">{t('chipFirmware.uploadHint')}</p>
          </section>

          {/* Updates */}
          <section>
            <h3 className={sectionTitle}>{t('chipFirmware.updatesTitle')}</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-3">
              <div>
                <span className="text-gray-500">{t('chipFirmware.runningVersion')}: </span>
                <span className="font-mono font-medium text-gray-900">
                  {jobsQuery.data?.chipVersion || t('chipFirmware.unknown')}
                </span>
              </div>
              <div>
                <span className="text-gray-500">{t('chipFirmware.lastOnline')}: </span>
                <span className="text-gray-900">
                  {jobsQuery.data?.chipLastOnlineAt
                    ? formatTime(jobsQuery.data.chipLastOnlineAt)
                    : t('chipFirmware.never')}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {restartMsg && <span className="text-gray-600">{restartMsg}</span>}
                <button type="button" onClick={handleRestart} disabled={restarting} className={secondaryBtn}>
                  <RotateCw className="w-4 h-4" />
                  {t('chipFirmware.restart')}
                </button>
              </div>
            </div>
            {jobsQuery.isError && (
              <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {serverError(jobsQuery.error, t('chipFirmware.loadJobsFailed'))}
              </div>
            )}
            <div className="overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['colVersion', 'colStatus', 'colMessage', 'colCreated', 'colFinished'].map((k) => (
                      <th
                        key={k}
                        className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {t(`chipFirmware.${k}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobsQuery.isLoading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 text-sm text-gray-500 text-center">
                        {t('common.loading')}
                      </td>
                    </tr>
                  ) : jobs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 text-sm text-gray-500 text-center">
                        {t('chipFirmware.noJobs')}
                      </td>
                    </tr>
                  ) : (
                    jobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-mono text-gray-900">{job.version}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              STATUS_BADGE[job.status] || STATUS_BADGE.pending
                            }`}
                          >
                            {STATUS_BADGE[job.status] ? t(`chipFirmware.status.${job.status}`) : job.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700 break-words max-w-xs">{job.message || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{formatTime(job.createdAt)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{formatTime(job.finishedAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Live logs */}
          <section>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className={`${sectionTitleBase} flex items-center gap-2`}>
                {t('chipFirmware.logsTitle')}
                {live && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 normal-case tracking-normal">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    {t('chipFirmware.logsLive')}
                  </span>
                )}
              </h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleToggleLive}
                  disabled={toggling}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 ${
                    live ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  }`}
                >
                  {live ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {live ? t('chipFirmware.stopLogs') : t('chipFirmware.startLogs')}
                </button>
                <button
                  type="button"
                  onClick={() => setLogLines([])}
                  disabled={logLines.length === 0}
                  className={secondaryBtn}
                >
                  <Trash2 className="w-4 h-4" />
                  {t('chipFirmware.clearLogs')}
                </button>
              </div>
            </div>
            {logError && (
              <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{logError}</div>
            )}
            <div
              ref={logBoxRef}
              onScroll={handleLogScroll}
              className="h-64 overflow-y-auto rounded-md bg-gray-900 text-gray-100 font-mono text-xs p-3"
            >
              {logLines.length === 0 ? (
                <div className="text-gray-500">
                  {live ? t('chipFirmware.logsWaiting') : t('chipFirmware.logsIdle')}
                </div>
              ) : (
                logLines.map((l) => (
                  <div key={l.seq} className="whitespace-pre-wrap break-all">
                    <span className="text-gray-500 select-none">{formatLogTime(l.at)} </span>
                    {l.line}
                  </div>
                ))
              )}
            </div>
            <p className="mt-2 text-sm text-gray-500">{t('chipFirmware.logsNote')}</p>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {t('chipFirmware.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChipFirmwareModal;
